import { Overlay, OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import type { OverlayTriggerType } from 'react-bootstrap/OverlayTrigger';
import type { PopperRef, Placement as ReactBootstrapPlacement } from 'react-bootstrap/types';

import styles from 'css/src/components/common/ui/Tooltip.module.scss';

import React from 'react';
import classNames from 'classnames';

import { isTesting } from 'js/src/libs/custom-utils';
import { isOnMobileDevice } from 'js/src/libs/mobile-check';

export enum Size {
    LARGE = 'large',
}

export enum PlacementEnum {
    TOP = 'top',
    BOTTOM = 'bottom',
    LEFT = 'left',
    RIGHT = 'right',
}

// Export for backwards compatibility - both as type and namespace
export type Placement = PlacementEnum;

export namespace Placement {
    export const TOP = PlacementEnum.TOP;
    export const BOTTOM = PlacementEnum.BOTTOM;
    export const LEFT = PlacementEnum.LEFT;
    export const RIGHT = PlacementEnum.RIGHT;
}

type Props = {
    onlyShowOnTruncate?: boolean; // Used with a child component that has text-overflow: ellipsis, to only show the tooltip if the text is truncated
    children?: React.ReactElement;
    title?: React.ReactNode;
    shortcut?: string | string[];
    animation?: boolean;
    placement?: PlacementEnum;
    trigger?: string;
    container?: HTMLElement | React.RefObject<HTMLElement | null> | string | null; // Type `string` is for backward compatibility. Try not to use it.
    html?: boolean;
    template?: string;
    viewport?: string | Element | null;
    delay?: number | { show: number; hide: number };
    show?: boolean;
    className?: string;
    anchorElm?: Element | null;
    fallbackPlacements?: readonly PlacementEnum[];
    size?: Size;
};

const isEllipsisActive = (e: Element) => {
    if (e instanceof HTMLElement) {
        return e.offsetWidth < e.scrollWidth || e.offsetHeight < e.scrollHeight;
    }
    return false;
};

/*
 * This is a wrapper using react-bootstrap OverlayTrigger and Tooltip components.
 *
 * Usage: wrap at least one child <Tooltip>{node}</Tooltip>.
 *
 * NOTE: the `show` prop controls whether or not a tooltip is displayed.
 * If set to false, no tooltip will be shown. This prop, along
 * with all the other props, are controlled.
 */
const TooltipShortcut = ({ shortcut }: { shortcut: string | string[] }) => {
    if (isOnMobileDevice()) {
        return null;
    }
    const keys = Array.isArray(shortcut) ? shortcut : [shortcut];
    return (
        <span className="l5-tooltip-shortcut">
            {keys.map((key, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <span className="l5-tooltip-shortcut-separator">+</span>}
                    <kbd className="l5-tooltip-kbd">{key}</kbd>
                </React.Fragment>
            ))}
        </span>
    );
};

const Tooltip = React.memo(function Tooltip({
    onlyShowOnTruncate,
    children,
    title,
    shortcut,
    animation = true,
    placement = PlacementEnum.TOP,
    trigger = 'hover focus',
    container,
    html = false,
    template,
    viewport,
    delay = 300,
    show = true,
    className,
    anchorElm,
    fallbackPlacements,
    size,
    ref: forwardedRef,
}: Props & { ref?: React.Ref<Element> }) {
    const resolvedTitle = React.useMemo(() => {
        if (!shortcut) {
            return title;
        }
        return (
            <>
                {title}
                <TooltipShortcut shortcut={shortcut} />
            </>
        );
    }, [title, shortcut]);
    const childRef = React.useRef<Element | null>(null);
    const [forceShow, setForceShow] = React.useState(false);
    // Kept in state as well as the ref for manual tooltips, so one shown from its first render re-renders
    // once its child mounts rather than anchoring to nothing.
    const [manualTarget, setManualTarget] = React.useState<Element | null>(null);

    const setRefs = React.useCallback(
        (element: Element | null) => {
            childRef.current = element;
            if (trigger === 'manual') {
                setManualTarget(element);
            }
            if (!forwardedRef) {
                return;
            }
            if (typeof forwardedRef === 'function') {
                forwardedRef(element);
            } else {
                forwardedRef.current = element;
            }
        },
        [forwardedRef, trigger],
    );

    const shouldShowTooltip = React.useMemo(() => {
        if (!show || !resolvedTitle) {
            return false;
        }

        if (onlyShowOnTruncate) {
            // For truncate tooltips, we'll check this dynamically in the OverlayTrigger
            return true;
        }

        return true;
    }, [show, resolvedTitle, onlyShowOnTruncate]);

    const triggerEvents = React.useMemo((): OverlayTriggerType[] => {
        if (trigger === 'manual') {
            return [];
        }
        const events = trigger.split(' ');
        const validEvents = events.filter((event): event is OverlayTriggerType =>
            ['hover', 'click', 'focus'].includes(event),
        );

        // Add 'focus' if only 'hover' is specified to prevent react-bootstrap warning
        if (validEvents.length === 1 && validEvents[0] === 'hover') {
            validEvents.push('focus');
        }

        return validEvents;
    }, [trigger]);

    const tooltipDelay = React.useMemo(() => {
        if (isTesting()) {
            return 0;
        }
        return delay;
    }, [delay]);

    const tooltipPlacement = React.useMemo(() => {
        return placement as ReactBootstrapPlacement;
    }, [placement]);

    const popperConfig = React.useMemo(() => {
        if (!fallbackPlacements) {
            return undefined;
        }
        return {
            modifiers: [
                {
                    name: 'flip',
                    enabled: true,
                    options: { fallbackPlacements: [...fallbackPlacements] },
                },
            ],
        };
    }, [fallbackPlacements]);

    const resolvedContainer = React.useMemo(():
        | HTMLElement
        | React.RefObject<HTMLElement | null>
        | null
        | undefined => {
        if (typeof container === 'string') {
            return document.querySelector<HTMLElement>(container);
        }
        return container;
    }, [container]);

    // Popper only repositions on scroll and resize, so a title that changes while the tooltip is open (one
    // previewing text as it's typed, say) would otherwise stay centred where the old one was.
    const popperRef = React.useRef<PopperRef | null>(null);
    React.useEffect(() => {
        popperRef.current?.scheduleUpdate?.();
    }, [resolvedTitle]);

    const renderTooltip = React.useCallback(
        (props: Record<string, unknown>) => {
            popperRef.current = (props.popper as PopperRef | undefined) ?? null;
            // Check ellipsis at render time for onlyShowOnTruncate
            if (onlyShowOnTruncate) {
                const element = anchorElm || childRef.current;
                if (!element || !isEllipsisActive(element)) {
                    return <div style={{ display: 'none' }} />;
                }
            }

            return (
                <BootstrapTooltip
                    {...props}
                    className={classNames(className, 'l5-tooltip', {
                        [styles['tooltip--lg']]: size === Size.LARGE,
                    })}
                    onMouseEnter={() => setForceShow(true)}
                    onMouseLeave={() => setForceShow(false)}
                >
                    {html && typeof title === 'string' ? (
                        <span dangerouslySetInnerHTML={{ __html: title }} />
                    ) : (
                        resolvedTitle
                    )}
                </BootstrapTooltip>
            );
        },
        [className, size, html, title, resolvedTitle, onlyShowOnTruncate, anchorElm],
    );

    const assertChildren = React.useCallback(() => {
        if (!children) {
            return;
        }
        const childrenArray = React.Children.toArray(children);
        if (childrenArray.length === 0) {
            return;
        }
        const firstChild = childrenArray[0];
        if (!React.isValidElement(firstChild)) {
            return;
        }
        if (!(firstChild.props as Record<string, unknown>).ref) {
            return;
        }
        throw new Error(
            'First child of the tooltip must not have a ref. Wrap the children in a div or other element. Or move the ref from the child to a parent outside the tooltip. Or use the anchorElm prop as done in https://github.com/Lumen5/web/pull/13992/files.',
        );
    }, [children]);

    if (!children) {
        return null;
    }

    assertChildren();

    if (!shouldShowTooltip) {
        return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
            ref: setRefs,
        });
    }

    // For manual triggers, show tooltip directly
    if (trigger === 'manual') {
        return (
            <>
                {React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
                    ref: setRefs,
                })}
                <Overlay
                    target={anchorElm || manualTarget}
                    show={show}
                    placement={tooltipPlacement}
                    container={resolvedContainer}
                    popperConfig={popperConfig}
                >
                    {renderTooltip}
                </Overlay>
            </>
        );
    }

    return (
        <OverlayTrigger
            placement={tooltipPlacement}
            delay={tooltipDelay}
            trigger={triggerEvents}
            overlay={renderTooltip}
            show={forceShow ? true : undefined}
            container={resolvedContainer}
            popperConfig={popperConfig}
        >
            {React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
                ref: setRefs,
            })}
        </OverlayTrigger>
    );
});

export default Tooltip;
